import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Collapse,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  People as PeopleIcon,
  Info as InfoIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';
import PropTypes from 'prop-types';

const ApiDuplicates = ({
  data,
  loading,
  pagination,
  onSearch,
  onRefresh,
  onView,
  onViewLead,
  onDelete,
  onSend,
  onOpenLeadHistory,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [expandedRows, setExpandedRows] = React.useState(new Set());
  const [openOtherDataModal, setOpenOtherDataModal] = React.useState(false);
  const [selectedOtherData, setSelectedOtherData] = React.useState(null);
  const debounceTimeoutRef = React.useRef(null);

  // Skeleton row component for loading state
  const SkeletonRow = () => (
    <TableRow>
      <TableCell>
        <Box>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="80%" height={16} />
          <Skeleton variant="text" width="70%" height={16} />
          <Skeleton variant="text" width="50%" height={16} />
        </Box>
      </TableCell>
      <TableCell>
        <Skeleton variant="rectangular" width={80} height={24} />
      </TableCell>
      <TableCell>
        <Skeleton variant="rectangular" width={100} height={24} />
      </TableCell>
      <TableCell>
        <Box>
          <Skeleton variant="text" width="50%" height={16} />
          <Skeleton variant="rectangular" width={100} height={20} />
        </Box>
      </TableCell>
      <TableCell>
        <Skeleton variant="text" width="80%" height={16} />
      </TableCell>
      <TableCell>
        <Box display="flex" gap={0.5}>
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
          <Skeleton variant="circular" width={32} height={32} />
        </Box>
      </TableCell>
    </TableRow>
  );

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      if (onSearch) {
        onSearch(value);
      }
    }, 300); // 300ms debounce
  };

  const handleRowExpand = (rowId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(rowId)) {
      newExpanded.delete(rowId);
    } else {
      newExpanded.add(rowId);
    }
    setExpandedRows(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Status is not editable or actionable in this view; we omit status UI

  const getSourceColor = (source) => {
    switch (source?.toLowerCase()) {
      case 'api':
        return 'primary';
      case 'manual':
        return 'secondary';
      case 'file_upload':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      {/* Search and Filter Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by name, email, phone, marks, MPC_4, or other data content..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={onRefresh}
                size="small"
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* API Duplicates Table */}
      <Card>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  Lead Info
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  Source
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  Integration Source
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  Other Data
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  Sent To
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  Received
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: pagination?.limit || 50 }).map((_, index) => (
                  <SkeletonRow key={index} />
                ))
              ) : (!data || data.length === 0) ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No data found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((duplicate) => (
                  <React.Fragment key={duplicate.id}>
                    <TableRow 
                      hover
                      sx={{
                        backgroundColor: (duplicate.customers_sent_to?.length === 1) ? '#fff3e0' : 'inherit',
                        '&:hover': {
                          backgroundColor: (duplicate.customers_sent_to?.length === 1) ? '#ffe0b2' : 'inherit',
                        },
                      }}
                    >
                  {/* Lead Information */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold" color="primary">
                        {duplicate.original_data?.fullName || duplicate.duplicate_data?.fullName || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        📧 {duplicate.original_data?.email || duplicate.duplicate_data?.email || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        📞 {duplicate.original_data?.phone || duplicate.duplicate_data?.phone || 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        🌍 {duplicate.original_data?.country || duplicate.duplicate_data?.country || 'N/A'}
                      </Typography>
                      {(duplicate.original_data?.MPC_4 || duplicate.duplicate_data?.MPC_4) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          📋 MPC_4: {duplicate.original_data?.MPC_4 || duplicate.duplicate_data?.MPC_4}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>

                  {/* Source */}
                  <TableCell>
                    <Chip
                      label={duplicate.original_data?.data_source || 'API'}
                      color={getSourceColor(duplicate.original_data?.data_source)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  {/* Integration Source */}
                  <TableCell>
                    {(duplicate.original_data?.integration_source || duplicate.duplicate_data?.integration_source) ? (
                      <Chip
                        label={duplicate.original_data?.integration_source || duplicate.duplicate_data?.integration_source}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>

                  {/* Other Data */}
                  <TableCell>
                    {(duplicate.original_data?.other_data || duplicate.duplicate_data?.other_data) ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setSelectedOtherData(duplicate.original_data?.other_data || duplicate.duplicate_data?.other_data);
                          setOpenOtherDataModal(true);
                        }}
                      >
                        Show
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>

                  {/* Sent To */}
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box>
                        {duplicate.customers_sent_to && duplicate.customers_sent_to.length > 0 ? (
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {duplicate.customers_sent_to.length} customer(s)
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.5}>
                              {duplicate.customers_sent_to.slice(0, 2).map((customer, index) => (
                                <Chip
                                  key={index}
                                  label={customer.customer_name || `Customer ${customer.customer_id}`}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ))}
                              {duplicate.customers_sent_to.length > 2 && (
                                <Chip
                                  label={`+${duplicate.customers_sent_to.length - 2} more`}
                                  size="small"
                                  color="default"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not sent yet
                          </Typography>
                        )}
                      </Box>
                      {duplicate.customers_sent_to && duplicate.customers_sent_to.length > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => handleRowExpand(duplicate.id)}
                        >
                          {expandedRows.has(duplicate.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>

                  {/* Received Date */}
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(duplicate.created_at)}
                    </Typography>
                  </TableCell>

                  {/* Actions */}
                  <TableCell>
                    <Box display="flex" gap={0.5} alignItems="center">
                      {(() => {
                        const count = Number(duplicate.lead_history_count || 0) || 0;
                        const title = count > 0 ? `${count} failure${count === 1 ? '' : 's'}` : 'No failures';
                        return (
                          <Tooltip title={title}>
                            <span>
                              {count > 0 && (
                                <Chip
                                  label={`×${count}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                  sx={{ height: '22px' }}
                                />
                              )}
                              <IconButton
                                size="small"
                                color={count > 0 ? 'error' : 'default'}
                                onClick={() => onOpenLeadHistory && onOpenLeadHistory(duplicate)}
                                sx={{ ml: count > 0 ? 0.5 : 0 }}
                              >
                                <InfoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        );
                      })()}
                      <Tooltip title="View Customer Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => onView && onView(duplicate)}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="View Lead">
                        <IconButton
                          size="small"
                          onClick={() => onViewLead && onViewLead(duplicate.original_data || duplicate.duplicate_data)}
                        >
                          <ViewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send to Customer">
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => onSend && onSend(duplicate)}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDelete && onDelete(duplicate)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  </TableRow>

                  {/* Expanded Row - Customer Details */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0 }}>
                      <Collapse in={expandedRows.has(duplicate.id)} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 2, backgroundColor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Customer Distribution Details
                          </Typography>
                          <Grid container spacing={2}>
                            {duplicate.customers_sent_to && duplicate.customers_sent_to.map((customer, index) => (
                              <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card variant="outlined" sx={{ p: 1 }}>
                                  <Box display="flex" alignItems="center" mb={1}>
                                    <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
                                    <Typography variant="body2" fontWeight="bold">
                                      {customer.customer_name || `Customer ${customer.customer_id}`}
                                    </Typography>
                                  </Box>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Customer ID: {customer.customer_id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Sent: {formatDate(customer.sent_at)}
                                  </Typography>
                                </Card>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Pagination Info */}
      {pagination && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Showing {data.length} of {pagination.total} API duplicates
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Page {pagination.page} of {pagination.totalPages}
          </Typography>
        </Box>
      )}

      {/* Other Data Modal */}
      <Dialog
        open={openOtherDataModal}
        onClose={() => {
          setOpenOtherDataModal(false);
          setSelectedOtherData(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6">Other Data</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {selectedOtherData ? (
              <TextField
                fullWidth
                multiline
                rows={15}
                value={
                  typeof selectedOtherData === 'object'
                    ? JSON.stringify(selectedOtherData, null, 2)
                    : String(selectedOtherData)
                }
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                  },
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No other data available
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenOtherDataModal(false);
              setSelectedOtherData(null);
            }}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ApiDuplicates.propTypes = {
  data: PropTypes.array,
  loading: PropTypes.bool,
  pagination: PropTypes.object,
  onSearch: PropTypes.func,
  onRefresh: PropTypes.func,
  onView: PropTypes.func,
  onViewLead: PropTypes.func,
  onDelete: PropTypes.func,
  onSend: PropTypes.func,
  onOpenLeadHistory: PropTypes.func,
};

export default ApiDuplicates;
